import { useSnackbar } from "notistack";
import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Stack,
  InputBase,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { CreateUser } from "../../hooks/useUserData";
import { GetUserGroupsByOrganisationId } from "../../hooks/useUserGroupdata";
import { GetFacilitiesByOrganisationId } from "../../hooks/useFacilityData";
import { getStoredRole, isUserAdminRole, ROLE_ADMIN } from "../../constants/roles";
import { useIsSuperAdmin } from "../../hooks/useIsSuperAdmin";

const INK = "#0a1628";
const INK_MUTED = "#5f6877";
const LINE = "#e6e4df";
const LINE_SOFT = "#eeece7";
const BRAND = "#8a1125";

const SectionLabel = ({ index, children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
    <Typography
      sx={{
        fontFamily: '"Outfit", sans-serif',
        fontSize: "11px",
        fontWeight: 600,
        color: BRAND,
        letterSpacing: "0.1em",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {index}
    </Typography>
    <Box sx={{ width: 14, height: "1px", background: LINE }} />
    <Typography
      sx={{
        fontSize: "10.5px",
        fontWeight: 700,
        letterSpacing: "0.24em",
        textTransform: "uppercase",
        color: INK_MUTED,
        fontFamily: '"DM Sans", sans-serif',
      }}
    >
      {children}
    </Typography>
  </Box>
);

const FieldLabel = ({ children, required }) => (
  <Typography
    sx={{
      fontSize: "11px",
      fontWeight: 600,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: INK_MUTED,
      mb: 1,
      fontFamily: '"DM Sans", sans-serif',
    }}
  >
    {children}
    {required && (
      <Box component="span" sx={{ color: BRAND, ml: 0.5 }}>
        *
      </Box>
    )}
  </Typography>
);

const TextInput = ({ value, onChange, placeholder, type = "text", autoFocus }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      px: 2,
      height: 48,
      background: "#fff",
      border: `1px solid ${LINE}`,
      borderRadius: "12px",
      transition: "all 0.25s ease",
      "&:hover": { borderColor: "rgba(10,22,40,0.2)" },
      "&:focus-within": {
        borderColor: "rgba(10,22,40,0.32)",
        boxShadow: "0 0 0 4px rgba(10,22,40,0.04)",
      },
    }}
  >
    <InputBase
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      type={type}
      autoFocus={autoFocus}
      fullWidth
      sx={{
        fontSize: "15px",
        fontWeight: 500,
        fontFamily: '"DM Sans", sans-serif',
        color: INK,
        "& input::placeholder": { color: INK_MUTED, opacity: 1 },
      }}
    />
  </Box>
);

const menuPaperProps = {
  PaperProps: {
    sx: {
      mt: 1,
      borderRadius: "12px",
      border: `1px solid ${LINE}`,
      boxShadow: "0 24px 48px -24px rgba(10,22,40,0.18)",
      "& .MuiMenuItem-root": {
        fontSize: "14px",
        fontFamily: '"DM Sans", sans-serif',
        py: 1.25,
        px: 2,
        "&.Mui-selected": {
          background: "rgba(10,22,40,0.04)",
          "&:hover": { background: "rgba(10,22,40,0.06)" },
        },
      },
    },
  },
};

const selectSx = {
  height: 48,
  background: "#fff",
  fontSize: "15px",
  fontWeight: 500,
  fontFamily: '"DM Sans", sans-serif',
  color: INK,
  "& .MuiSelect-select": {
    py: 0,
    display: "flex",
    alignItems: "center",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: LINE,
    borderRadius: "12px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(10,22,40,0.2)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(10,22,40,0.32)",
    borderWidth: "1px",
  },
  "& .MuiSelect-icon": { color: INK_MUTED },
};

export const CreateUserModal = (props) => {
  const { enqueueSnackbar } = useSnackbar();

  const [firstName, setFirstname] = useState("");
  const [lastName, setLastname] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [group, setGroup] = useState("");
  const [facility, setFacility] = useState("");
  const [role, setRole] = useState("user");

  const organisationId = props.customerid;
  // Callers opt into the role picker via showRoleSelect (the admin user view
  // and the superadmin organisation view both do). When omitted, `role` is left
  // out of the payload and the backend defaults it to 'user'.
  // Role-assignment ladder (mirrors the backend): superadmin → admin,
  // admin → user_admin, user_admin → user only. A user_admin creator therefore
  // gets no picker at all (backend enforces role='user' regardless).
  const { isSuperAdmin } = useIsSuperAdmin();
  const creatorRole = getStoredRole();
  const creatorIsUserAdmin = isUserAdminRole(creatorRole);
  const showRoleSelect = !!props.showRoleSelect && !creatorIsUserAdmin;
  const canAssignAdmin = isSuperAdmin; // only superadmin may create admins
  const canAssignUserAdmin = isSuperAdmin || creatorRole === ROLE_ADMIN;

  const { data: userGroupData } = GetUserGroupsByOrganisationId(organisationId);
  const { data: facilitiesData } = GetFacilitiesByOrganisationId(organisationId);

  const groups = useMemo(
    () =>
      (userGroupData?.data || []).map((g) => ({
        id: g.id,
        label: g.group_name,
      })),
    [userGroupData]
  );

  const facilities = useMemo(
    () =>
      (facilitiesData?.data || []).map((f) => ({
        id: f.id,
        label: f.facility_name,
      })),
    [facilitiesData]
  );

  const createUserMutation = CreateUser({
    onSuccess: () => {
      enqueueSnackbar("Användare skapad.", { variant: "success" });
      props.closeCreateModal && props.closeCreateModal();
    },
    onError: () => {
      enqueueSnackbar("Kunde inte skapa användare.", { variant: "error" });
    },
  });

  const handleCreate = () => {
    createUserMutation.mutate({
      organisation_id: organisationId,
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      email: email.trim(),
      phone: phone.trim(),
      group_id: group,
      facility_id: facility || null,
      ...(showRoleSelect ? { role } : {}),
    });
  };

  const saving = createUserMutation.isLoading;
  const canSave =
    firstName.trim() &&
    lastName.trim() &&
    email.trim() &&
    phone.trim() &&
    group &&
    !saving;

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <SectionLabel index="01">Personuppgifter</SectionLabel>
        <Stack spacing={2.25}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Box>
              <FieldLabel required>Förnamn</FieldLabel>
              <TextInput
                value={firstName}
                onChange={(e) => setFirstname(e.target.value)}
                placeholder="T.ex. Anna"
                autoFocus
              />
            </Box>
            <Box>
              <FieldLabel required>Efternamn</FieldLabel>
              <TextInput
                value={lastName}
                onChange={(e) => setLastname(e.target.value)}
                placeholder="T.ex. Svensson"
              />
            </Box>
          </Box>
          <Box>
            <FieldLabel required>E-postadress</FieldLabel>
            <TextInput
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="namn@företag.se"
              type="email"
            />
          </Box>
          <Box>
            <FieldLabel required>Telefonnummer</FieldLabel>
            <TextInput
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="0701234567"
              type="tel"
            />
          </Box>
        </Stack>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionLabel index="02">Tillhörighet</SectionLabel>
        <Stack spacing={2.25}>
          {showRoleSelect && (
            <Box>
              <FieldLabel required>Roll</FieldLabel>
              <FormControl fullWidth>
                <Select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  IconComponent={KeyboardArrowDownRoundedIcon}
                  MenuProps={menuPaperProps}
                  sx={selectSx}
                >
                  <MenuItem value="user">Vanlig användare</MenuItem>
                  {canAssignUserAdmin && (
                    <MenuItem value="user_admin">Användaradmin</MenuItem>
                  )}
                  {canAssignAdmin && <MenuItem value="admin">Admin</MenuItem>}
                </Select>
              </FormControl>
              <Typography
                sx={{
                  mt: 0.75,
                  fontSize: "12px",
                  color: INK_MUTED,
                  fontFamily: '"DM Sans", sans-serif',
                }}
              >
                {role === "admin"
                  ? "Kan logga in i webbverktyget och hantera organisationen."
                  : "Endast mobilappen — ingen åtkomst till webbverktyget."}
              </Typography>
            </Box>
          )}
          <Box>
            <FieldLabel required>Grupp</FieldLabel>
            <FormControl fullWidth>
              <Select
                value={group}
                onChange={(e) => setGroup(e.target.value)}
                displayEmpty
                IconComponent={KeyboardArrowDownRoundedIcon}
                MenuProps={menuPaperProps}
                sx={selectSx}
                renderValue={(v) => {
                  if (!v)
                    return (
                      <Box sx={{ color: INK_MUTED, fontStyle: "italic" }}>
                        Välj grupp
                      </Box>
                    );
                  return groups.find((g) => g.id === v)?.label || "";
                }}
              >
                <MenuItem value="" disabled>
                  <Box sx={{ color: INK_MUTED, fontStyle: "italic" }}>
                    Välj grupp
                  </Box>
                </MenuItem>
                {groups.map((g) => (
                  <MenuItem key={g.id} value={g.id}>
                    {g.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <FieldLabel>Anläggning</FieldLabel>
            <FormControl fullWidth>
              <Select
                value={facility}
                onChange={(e) => setFacility(e.target.value)}
                displayEmpty
                IconComponent={KeyboardArrowDownRoundedIcon}
                MenuProps={menuPaperProps}
                sx={selectSx}
                renderValue={(v) => {
                  if (!v)
                    return (
                      <Box sx={{ color: INK_MUTED, fontStyle: "italic" }}>
                        Ingen anläggning
                      </Box>
                    );
                  return facilities.find((f) => f.id === v)?.label || "";
                }}
              >
                <MenuItem value="">
                  <Box sx={{ color: INK_MUTED, fontStyle: "italic" }}>
                    Ingen anläggning
                  </Box>
                </MenuItem>
                {facilities.map((f) => (
                  <MenuItem key={f.id} value={f.id}>
                    {f.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Stack>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.25,
          pt: 3,
          borderTop: `1px solid ${LINE_SOFT}`,
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={() => props.closeCreateModal && props.closeCreateModal()}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 2.25,
            py: 1.25,
            background: "transparent",
            color: INK_MUTED,
            border: `1px solid ${LINE}`,
            borderRadius: "10px",
            fontFamily: '"DM Sans", sans-serif',
            fontSize: "13px",
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.25s ease",
            "&:hover": {
              color: INK,
              borderColor: "rgba(10,22,40,0.22)",
            },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: 16 }} />
          Avbryt
        </Box>
        <Box
          component="button"
          type="button"
          onClick={handleCreate}
          disabled={!canSave}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 2.75,
            py: 1.25,
            background: INK,
            color: "#fff",
            border: "none",
            borderRadius: "10px",
            fontFamily: '"DM Sans", sans-serif',
            fontSize: "13px",
            fontWeight: 600,
            cursor: canSave ? "pointer" : "not-allowed",
            opacity: canSave ? 1 : 0.4,
            transition: "all 0.25s ease",
            boxShadow: canSave ? "0 10px 24px -12px rgba(10,22,40,0.55)" : "none",
            "&:hover": canSave
              ? { background: "#1a2638", transform: "translateY(-1px)" }
              : {},
          }}
        >
          {saving ? (
            <CircularProgress size={14} sx={{ color: "#fff" }} />
          ) : (
            <CheckRoundedIcon sx={{ fontSize: 16 }} />
          )}
          Skapa användare
        </Box>
      </Box>
    </Box>
  );
};
